import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { z } from "zod";
import {
  getCalendarMonthWindow,
  getUtcMonthsForWindow,
} from "../../../api/src/calendly/months";
import type { PublicAvailabilitySnapshot } from "../../../api/src/calendly/snapshot-preview";
import { getViteEnv } from "../config";
import { getNativeFetch } from "../utils/nativeFetch";

const snapshotSchema: z.ZodType<
  PublicAvailabilitySnapshot,
  z.ZodTypeDef,
  unknown
> = z
  .object({
    schemaVersion: z.literal(2),
    month: z.string(),
    timeZone: z.literal("UTC"),
    scope: z.discriminatedUnion("type", [
      z.object({ type: z.literal("nationwide") }),
      z.object({ type: z.literal("state"), state: z.string() }),
    ]),
    productType: z.enum(["Q_A", "REPRESENTATION", "POSTNUP_REPRESENTATION"]),
    windowStartTime: z.string().datetime(),
    windowEndTime: z.string().datetime(),
    generatedAt: z.string().datetime(),
    sourceUpdatedAt: z.string().datetime(),
    staleAfter: z.string().datetime(),
    totalEligibleAttorneys: z.number().int().nonnegative(),
    coverage: z
      .object({
        status: z.enum(["complete", "partial", "unavailable"]),
        eligibleServiceCount: z.number().int().nonnegative(),
        coveredServiceCount: z.number().int().nonnegative(),
        missingServiceCount: z.number().int().nonnegative(),
        unmatchedBookingUrlServiceCount: z.number().int().nonnegative(),
        missingMonthSourceServiceCount: z.number().int().nonnegative(),
        eligibleAttorneyCount: z.number().int().nonnegative(),
        coveredAttorneyCount: z.number().int().nonnegative(),
        missingAttorneyCount: z.number().int().nonnegative(),
      })
      .optional(),
    slots: z.array(
      z.object({
        startTime: z.string().datetime(),
        availableAttorneyCount: z.number().int().nonnegative(),
      }),
    ),
  })
  .transform(({ coverage, ...snapshot }) => ({
    ...snapshot,
    ...(coverage ? { coverage } : {}),
  }));

interface PublicAvailabilityQuery {
  month: string;
  timeZone: string;
  state?: string;
  productType: PublicAvailabilitySnapshot["productType"];
}

export const attorneyAvailabilityApi = createApi({
  reducerPath: "attorneyAvailabilityApi",
  baseQuery: fetchBaseQuery({ fetchFn: getNativeFetch(), credentials: "omit" }),
  endpoints: (builder) => ({
    getPublicAttorneyAvailability: builder.query<
      PublicAvailabilitySnapshot[],
      PublicAvailabilityQuery
    >({
      async queryFn(
        { month, timeZone, state, productType },
        _api,
        _options,
        fetch,
      ) {
        const baseUrl = getViteEnv(
          "VITE_CALENDLY_AVAILABILITY_BASE_URL",
        )?.replace(/\/+$/u, "");
        if (!baseUrl)
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: "Availability is not configured",
            },
          };
        const window = getCalendarMonthWindow(month, timeZone);
        const startTime = new Date(
          Math.max(window.startTime.getTime(), Date.now()),
        );
        const months = getUtcMonthsForWindow(startTime, window.endTime);
        const scope = state
          ? `states/${encodeURIComponent(state)}`
          : "nationwide";
        const results = await Promise.all(
          months.map(async (utcMonth) => {
            const result = await fetch(
              `${baseUrl}/calendly/availability/v2/${scope}/${productType}/${utcMonth}.json`,
            );
            if (result.error) return { error: result.error };
            const parsed = snapshotSchema.safeParse(result.data);
            const utcWindow = getCalendarMonthWindow(utcMonth);
            if (
              !parsed.success ||
              parsed.data.month !== utcMonth ||
              parsed.data.productType !== productType ||
              (state
                ? parsed.data.scope.type !== "state" ||
                  parsed.data.scope.state !== state
                : parsed.data.scope.type !== "nationwide") ||
              Date.parse(parsed.data.windowStartTime) >=
                utcWindow.endTime.getTime() ||
              Date.parse(parsed.data.windowEndTime) <
                utcWindow.endTime.getTime()
            ) {
              return {
                error: {
                  status: "CUSTOM_ERROR" as const,
                  error: "Invalid availability snapshot",
                },
              };
            }
            return { data: parsed.data };
          }),
        );
        const error = results.find((result) => result.error)?.error;
        if (error) return { error };
        return {
          data: results.flatMap((result) => (result.data ? [result.data] : [])),
        };
      },
    }),
  }),
});

export const { useGetPublicAttorneyAvailabilityQuery } =
  attorneyAvailabilityApi;
